import React, { useState, useMemo } from 'react';
import { X, Download, Calendar, Users, Loader2, CheckSquare, Square } from 'lucide-react';
import DatePicker, { registerLocale } from 'react-datepicker';
import el from 'date-fns/locale/el';
import 'react-datepicker/dist/react-datepicker.css';
import { calculateDaysBetween } from '../utils/vacationUtils';

registerLocale('el', el);

// ─── Helpers ────────────────────────────────────────────────────────────────

const translateStatus = (status) => {
  switch (status) {
    case 'approved':           return 'Εγκρίθηκε';
    case 'pending':            return 'Σε εκκρεμότητα';
    case 'manager1_approved':  return 'Μερικώς Εγκεκριμένη (Δ1)';
    case 'manager1_rejected':  return 'Απορρίφθηκε (Δ1)';
    case 'rejected':           return 'Απορρίφθηκε';
    default:                   return status || '—';
  }
};

const statusColor = (status) => {
  switch (status) {
    case 'approved':          return '#16a34a';
    case 'rejected':
    case 'manager1_rejected': return '#dc2626';
    case 'pending':           return '#d97706';
    case 'manager1_approved': return '#2563eb';
    default:                  return '#6b7280';
  }
};

const statusBg = (status) => {
  switch (status) {
    case 'approved':          return '#f0fdf4';
    case 'rejected':
    case 'manager1_rejected': return '#fef2f2';
    case 'pending':           return '#fffbeb';
    case 'manager1_approved': return '#eff6ff';
    default:                  return '#f9fafb';
  }
};

const leaveTypeLabel = (type, isGranted = false) => {
  switch ((type || '').toLowerCase()) {
    case 'vacation':    return 'Κανονική Άδεια';
    case 'mandatory':   return 'Υποχρεωτική Άδεια';
    case 'sick':        return 'Ασθένεια';
    case 'maternity':   return 'Μητρότητα';
    case 'paternity':   return 'Πατρότητα';
    case 'unpaid':      return isGranted ? 'Χορηγήθηκε - Άνευ Αποδοχών' : 'Άνευ Αποδοχών';
    case 'unjustified': return 'Αδικαιολόγητη Απουσία';
    default:            return type || 'Κανονική Άδεια';
  }
};

// ─── PDF generation via browser print (full Greek support) ─────────────────

const generateAndPrintPDF = ({ requests, userDatabase, departments, holidays, startDate, endDate }) => {
  const dateStr   = new Date().toLocaleDateString('el-GR');
  const periodStr = `${startDate.toLocaleDateString('el-GR')} – ${endDate.toLocaleDateString('el-GR')}`;

  // Group by employee, preserving sort order
  const byUser = {};
  const userOrder = [];
  requests.forEach(req => {
    const uid = String(req.user_id || req.userId);
    if (!byUser[uid]) { byUser[uid] = []; userOrder.push(uid); }
    byUser[uid].push(req);
  });

  // Each employee gets its own self-contained block (div + table)
  // so page-break-inside:avoid works reliably across all browsers/PDF engines
  const blocksHTML = userOrder.map(uid => {
    const reqs = byUser[uid];
    const user = userDatabase.find(u => Number(u.id) === Number(uid));
    const dept = departments.find(d => d.id === (user?.department_id || user?.departmentId));
    const initials = (user?.name || '?').split(' ').map(n => n[0]).join('').toUpperCase();
    const color    = dept?.color || '#3B82F6';

    const dataRows = reqs.map((req, i) => {
      const start       = new Date(req.start_date || req.startDate);
      const end         = new Date(req.end_date   || req.endDate);
      const days        = calculateDaysBetween(start, end, holidays);
      const st          = req.status;
      const isGranted   = req.manager_granted || req.managerGranted;
      const isUnpaid    = (req.leave_type || req.leaveType) === 'unpaid';
      const type        = isGranted ? (isUnpaid ? 'unpaid' : 'mandatory') : (req.leave_type || req.leaveType || 'vacation');
      const statusLabel = isGranted ? 'Χορηγήθηκε' : translateStatus(st);
      const sc          = isGranted ? (isUnpaid ? '#ea580c' : '#7c3aed') : statusColor(st);
      const sb          = isGranted ? (isUnpaid ? '#fff7ed' : '#f5f3ff') : statusBg(st);
      const rowBg       = i % 2 === 0 ? '#ffffff' : '#f8fafc';

      return `
        <tr style="background:${rowBg};">
          <td class="cell">${start.toLocaleDateString('el-GR', { weekday:'short', day:'numeric', month:'short', year:'numeric' })}</td>
          <td class="cell">${end.toLocaleDateString('el-GR',   { weekday:'short', day:'numeric', month:'short', year:'numeric' })}</td>
          <td class="cell" style="text-align:center;font-weight:700;color:#1d4ed8;">${days}</td>
          <td class="cell" style="text-align:center;">
            <span style="display:inline-block;padding:4px 12px;border-radius:999px;
                         background:${sb};color:${sc};font-weight:700;
                         border:1px solid ${sc}55;white-space:nowrap;font-size:13px;">
              ${statusLabel}
            </span>
          </td>
          <td class="cell" style="color:#6b7280;white-space:normal;">${leaveTypeLabel(type, isGranted)}</td>
          <td class="cell reason">${req.reason || '—'}</td>
        </tr>`;
    }).join('');

    return `
      <div class="employee-block">
        <div class="emp-header" style="border-left:4px solid ${color};background:linear-gradient(135deg,${color}18,${color}05);">
          <div class="emp-avatar" style="background:${color};">${initials}</div>
          <div>
            <div class="emp-name">${user?.name || 'Άγνωστος'}</div>
            <div class="emp-meta">${dept?.name || 'Χωρίς Τμήμα'} &nbsp;·&nbsp; ${reqs.length} αίτηση${reqs.length !== 1 ? 'εις' : ''}</div>
          </div>
        </div>
        <table>
          <colgroup>
            <col style="width:150px;">
            <col style="width:150px;">
            <col style="width:60px;">
            <col style="width:150px;">
            <col style="width:175px;">
            <col>
          </colgroup>
          <thead>
            <tr class="thead-row">
              <th class="th">Από</th>
              <th class="th">Έως</th>
              <th class="th" style="text-align:center;">Εργ. Ημ.</th>
              <th class="th" style="text-align:center;">Κατάσταση</th>
              <th class="th">Τύπος</th>
              <th class="th">Αιτιολογία</th>
            </tr>
          </thead>
          <tbody>
            ${dataRows}
          </tbody>
        </table>
      </div>`;
  }).join('');

  const totalRequests = requests.length;
  const totalDays     = requests.reduce((sum, req) =>
    sum + calculateDaysBetween(new Date(req.start_date || req.startDate), new Date(req.end_date || req.endDate), holidays), 0);
  const totalApproved = requests.filter(r => r.status === 'approved').length;
  const totalPending  = requests.filter(r => r.status === 'pending' || r.status === 'manager1_approved').length;
  const totalUnpaid   = requests.filter(r => (r.leave_type || r.leaveType) === 'unpaid').length;

  const html = `<!DOCTYPE html>
<html lang="el">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width,initial-scale=1">
  <title>Αναφορά Αδειών – ${periodStr}</title>
  <style>
    @media print {
      @page { size: A4 landscape; margin: 12mm 14mm; }
      .no-print { display: none !important; }
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .employee-block { page-break-inside: avoid; break-inside: avoid; }
    }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: "Segoe UI", "Helvetica Neue", Arial, sans-serif; background:#fff; color:#111827; padding:24px; }
    .report-header { display:flex; justify-content:space-between; align-items:flex-end; border-bottom:3px solid #2563eb; padding-bottom:16px; margin-bottom:24px; }
    .report-title   { font-size:22px; font-weight:900; color:#111827; }
    .report-subtitle{ font-size:12px; color:#6b7280; margin-top:4px; }
    .report-meta    { text-align:right; font-size:11px; color:#9ca3af; line-height:1.7; }
    .summary-grid   { display:grid; grid-template-columns:repeat(5,1fr); gap:12px; margin-bottom:28px; }
    .summary-card   { border:1px solid #e5e7eb; border-radius:10px; padding:14px 16px; background:#f9fafb; }
    .summary-card .label { font-size:12px; font-weight:700; color:#6b7280; text-transform:uppercase; letter-spacing:.05em; }
    .summary-card .value { font-size:28px; font-weight:900; margin-top:4px; }
    .employee-block { margin-bottom:20px; border:1px solid #e5e7eb; border-radius:10px; overflow:hidden; }
    .emp-header { display:flex; align-items:center; gap:12px; padding:12px 16px; }
    .emp-avatar { width:38px; height:38px; border-radius:50%; color:#fff; display:flex; align-items:center; justify-content:center; font-weight:900; font-size:13px; flex-shrink:0; }
    .emp-name   { font-weight:800; font-size:14px; color:#111827; }
    .emp-meta   { font-size:12px; color:#6b7280; margin-top:2px; }
    table       { width:100%; border-collapse:collapse; table-layout:fixed; }
    .thead-row  { background:#f1f5f9; }
    .th         { padding:8px 14px; font-size:12px; font-weight:700; color:#64748b; text-transform:uppercase; letter-spacing:.05em; border-bottom:1px solid #e2e8f0; white-space:nowrap; text-align:left; }
    .cell       { padding:10px 14px; font-size:14px; color:#374151; border-bottom:1px solid #f1f5f9; white-space:nowrap; overflow:hidden; }
    .reason     { white-space:normal; word-break:break-word; font-size:13px; color:#6b7280; }
    .print-btn  { display:inline-flex; align-items:center; gap:8px; background:#2563eb; color:#fff; border:none; border-radius:8px; padding:10px 22px; font-size:14px; font-weight:700; cursor:pointer; margin-bottom:24px; }
    .print-btn:hover { background:#1d4ed8; }
  </style>
</head>
<body>
  <button class="print-btn no-print" onclick="window.print()">🖨️ Εκτύπωση / Αποθήκευση PDF</button>
  <div class="report-header">
    <div>
      <div class="report-title">Αναφορά Αδειών Προσωπικού</div>
      <div class="report-subtitle">Περίοδος: ${periodStr}</div>
    </div>
    <div class="report-meta">Ημ. Έκδοσης: ${dateStr}<br>Σύστημα Διαχείρισης Αδειών</div>
  </div>
  <div class="summary-grid">
    <div class="summary-card"><div class="label">Συνολικά Αιτήματα</div><div class="value" style="color:#2563eb;">${totalRequests}</div></div>
    <div class="summary-card"><div class="label">Συνολικές Εργ. Ημέρες</div><div class="value" style="color:#7c3aed;">${totalDays}</div></div>
    <div class="summary-card"><div class="label">Εγκεκριμένα</div><div class="value" style="color:#16a34a;">${totalApproved}</div></div>
    <div class="summary-card"><div class="label">Εκκρεμή</div><div class="value" style="color:#d97706;">${totalPending}</div></div>
    <div class="summary-card"><div class="label">Άνευ Αποδοχών</div><div class="value" style="color:#ea580c;">${totalUnpaid}</div></div>
  </div>
  ${blocksHTML}
  <div style="margin-top:32px;border-top:1px solid #e5e7eb;padding-top:12px;font-size:12px;color:#9ca3af;text-align:center;">
    Ημερολόγιο Αδειών — Αναφορά παράχθηκε αυτόματα στις ${dateStr}
  </div>
</body>
</html>`;

  const win = window.open('', '_blank');
  if (win) { win.document.write(html); win.document.close(); }
};

// ─── Modal Component ─────────────────────────────────────────────────────────


const MassExportModal = ({
  isOpen,
  onClose,
  userDatabase = [],
  departments  = [],
  vacationRequests = [],
  holidays = [],
}) => {
  const [startDate, setStartDate]       = useState(null);
  const [endDate,   setEndDate]         = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'approved' | 'pending' | 'unpaid'
  const [isExporting, setIsExporting]   = useState(false);

  const allEmployees = useMemo(() => userDatabase, [userDatabase]);

  const groupedByDept = useMemo(() =>
    departments
      .map(dept => ({
        dept,
        employees: allEmployees.filter(
          u => (u.department_id || u.departmentId) === dept.id
        ),
      }))
      .filter(g => g.employees.length > 0),
    [departments, allEmployees]
  );

  // Counts for the "matched" indicator
  const matchedCount = useMemo(() => {
    if (!startDate || !endDate || selectedUsers.length === 0) return 0;
    return vacationRequests.filter(req => {
      const uid = req.user_id || req.userId;
      if (!selectedUsers.includes(uid)) return false;
      const s = new Date(req.start_date || req.startDate);
      const e = new Date(req.end_date   || req.endDate);
      const selS = new Date(startDate); selS.setHours(0,0,0,0);
      const selE = new Date(endDate);   selE.setHours(23,59,59,999);
      const inRange = s <= selE && e >= selS;
      if (!inRange) return false;
      if (statusFilter === 'approved') return req.status === 'approved';
      if (statusFilter === 'pending')  return req.status === 'pending' || req.status === 'manager1_approved';
      if (statusFilter === 'unpaid')   return (req.leave_type || req.leaveType) === 'unpaid';
      return true;
    }).length;
  }, [vacationRequests, selectedUsers, startDate, endDate, statusFilter]);

  const handleToggleUser  = (id) => setSelectedUsers(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const handleToggleDept  = (employees) => {
    const ids = employees.map(u => u.id);
    const allSelected = ids.every(id => selectedUsers.includes(id));
    setSelectedUsers(p =>
      allSelected ? p.filter(id => !ids.includes(id)) : [...new Set([...p, ...ids])]
    );
  };
  const handleSelectAll   = () => setSelectedUsers(allEmployees.map(u => u.id));
  const handleDeselectAll = () => setSelectedUsers([]);

  const handleExport = () => {
    if (!startDate || !endDate) { alert('Παρακαλώ επιλέξτε χρονικό διάστημα.'); return; }
    if (selectedUsers.length === 0) { alert('Παρακαλώ επιλέξτε τουλάχιστον έναν υπάλληλο.'); return; }
    if (matchedCount === 0) { alert('Δεν βρέθηκαν αιτήματα για τα επιλεγμένα κριτήρια.'); return; }

    setIsExporting(true);

    const selS = new Date(startDate); selS.setHours(0,0,0,0);
    const selE = new Date(endDate);   selE.setHours(23,59,59,999);

    const filtered = vacationRequests.filter(req => {
      const uid = req.user_id || req.userId;
      if (!selectedUsers.includes(uid)) return false;
      const s = new Date(req.start_date || req.startDate);
      const e = new Date(req.end_date   || req.endDate);
      const inRange = s <= selE && e >= selS;
      if (!inRange) return false;
      if (statusFilter === 'approved') return req.status === 'approved';
      if (statusFilter === 'pending')  return req.status === 'pending' || req.status === 'manager1_approved';
      if (statusFilter === 'unpaid')   return (req.leave_type || req.leaveType) === 'unpaid';
      return true;
    });

    // Sort: by employee name, then start date
    filtered.sort((a, b) => {
      const uA = userDatabase.find(u => Number(u.id) === Number(a.user_id || a.userId));
      const uB = userDatabase.find(u => Number(u.id) === Number(b.user_id || b.userId));
      const nameComp = (uA?.name || '').localeCompare(uB?.name || '', 'el');
      if (nameComp !== 0) return nameComp;
      return new Date(a.start_date || a.startDate) - new Date(b.start_date || b.startDate);
    });

    generateAndPrintPDF({ requests: filtered, userDatabase, departments, holidays, startDate, endDate });
    setIsExporting(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[100] backdrop-blur-sm">
      <div className="bg-white dark:bg-jewel-bg rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b dark:border-jewel-border bg-gray-50 dark:bg-jewel-surface rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="bg-blue-600 dark:bg-jewel-teal p-2 rounded-lg">
              <Download className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-base font-black text-gray-900 dark:text-jewel-text">Μαζική Εξαγωγή Αναφοράς</h2>
              <p className="text-xs text-gray-500 dark:text-jewel-text-muted">Επιλέξτε περίοδο, υπαλλήλους και κατάσταση</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-200 dark:hover:bg-jewel-surface rounded-lg transition-colors">
            <X className="h-5 w-5 text-gray-500 dark:text-jewel-text-muted" />
          </button>
        </div>
        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* LEFT: Filters */}
            <div className="space-y-5">
              {/* Date range */}
              <div>
                <label className="text-[10px] font-black text-gray-400 dark:text-jewel-text-muted uppercase tracking-wider block mb-2">
                  <Calendar className="inline h-3 w-3 mr-1" />Χρονικό Διάστημα
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <DatePicker
                    selected={startDate}
                    onChange={setStartDate}
                    selectsStart startDate={startDate} endDate={endDate}
                    locale="el" dateFormat="dd/MM/yyyy"
                    placeholderText="Από"
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-jewel-border bg-white dark:bg-jewel-surface text-gray-900 dark:text-jewel-text rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <DatePicker
                    selected={endDate}
                    onChange={setEndDate}
                    selectsEnd startDate={startDate} endDate={endDate}
                    minDate={startDate}
                    locale="el" dateFormat="dd/MM/yyyy"
                    placeholderText="Έως"
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-jewel-border bg-white dark:bg-jewel-surface text-gray-900 dark:text-jewel-text rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              {/* Status filter */}
              <div>
                <label className="text-[10px] font-black text-gray-400 dark:text-jewel-text-muted uppercase tracking-wider block mb-2">
                  Κατάσταση Αιτημάτων
                </label>
                <div className="flex gap-2 flex-wrap">
                  {[
                    { id: 'all',      label: 'Όλες' },
                    { id: 'approved', label: 'Εγκεκριμένες' },
                    { id: 'pending',  label: 'Εκκρεμείς' },
                    { id: 'unpaid',   label: 'Άνευ Αποδοχών' },
                  ].map(opt => (
                    <button
                      key={opt.id}
                      onClick={() => setStatusFilter(opt.id)}
                      className={`flex-1 py-2 px-3 text-xs font-bold rounded-lg border-2 transition-all whitespace-nowrap ${
                        statusFilter === opt.id
                          ? (opt.id === 'unpaid' ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400' : 'border-blue-500 dark:border-jewel-teal bg-blue-50 dark:bg-jewel-teal/20 text-blue-700 dark:text-jewel-teal-tint')
                          : 'border-gray-200 dark:border-jewel-border bg-white dark:bg-jewel-surface text-gray-500 dark:text-jewel-text-muted hover:border-gray-300 dark:hover:border-jewel-text-muted'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              {/* Matched count preview */}
              <div className={`rounded-xl p-4 border-2 transition-all ${
                matchedCount > 0
                  ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20'
                  : 'border-gray-200 dark:border-jewel-border bg-gray-50 dark:bg-jewel-surface'
              }`}>
                <p className="text-[10px] font-black uppercase text-gray-400 dark:text-jewel-text-muted mb-1">Αποτελέσματα Προεπισκόπησης</p>
                <p className={`text-3xl font-black ${matchedCount > 0 ? 'text-green-600 dark:text-green-400' : 'text-gray-300 dark:text-jewel-text-muted'}`}>
                  {matchedCount}
                </p>
                <p className="text-xs text-gray-500 dark:text-jewel-text-muted mt-0.5">
                  αίτηση{matchedCount !== 1 ? 'εις' : ''} θα συμπεριληφθ{matchedCount !== 1 ? 'ούν' : 'εί'}
                </p>
              </div>
            </div>
            {/* RIGHT: Employee selection */}
            <div className="flex flex-col">
              <div className="flex items-center justify-between mb-2">
                <label className="text-[10px] font-black text-gray-400 dark:text-jewel-text-muted uppercase tracking-wider flex items-center gap-1">
                  <Users className="h-3 w-3" /> Υπάλληλοι ({selectedUsers.length}/{allEmployees.length})
                </label>
                <div className="flex gap-2">
                  <button onClick={handleSelectAll}   className="text-[10px] text-blue-600 dark:text-jewel-teal-tint font-bold hover:underline">Όλοι</button>
                  <span className="text-gray-300 dark:text-jewel-border">|</span>
                  <button onClick={handleDeselectAll} className="text-[10px] text-gray-500 dark:text-jewel-text-muted font-bold hover:underline">Κανένας</button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto border border-gray-200 dark:border-jewel-border rounded-xl max-h-[42vh] divide-y divide-gray-100 dark:divide-jewel-border">
                {groupedByDept.map(({ dept, employees }) => {
                  const deptIds      = employees.map(u => u.id);
                  const allChecked   = deptIds.every(id => selectedUsers.includes(id));
                  const someChecked  = deptIds.some(id => selectedUsers.includes(id)) && !allChecked;
                  return (
                    <div key={dept.id}>
                      {/* Department header */}
                      <button
                        onClick={() => handleToggleDept(employees)}
                        className="w-full flex items-center gap-2 px-3 py-2.5 bg-gray-50 dark:bg-jewel-surface hover:bg-gray-100 dark:hover:bg-jewel-border transition-colors text-left"
                      >
                        <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: dept.color }} />
                        <span className="text-xs font-bold text-gray-700 dark:text-jewel-text flex-1">{dept.name}</span>
                        <span className="text-[10px] text-gray-400 dark:text-jewel-text-muted">({employees.length})</span>
                        {allChecked
                          ? <CheckSquare className="h-4 w-4 text-blue-600 dark:text-jewel-teal-tint flex-shrink-0" />
                          : someChecked
                          ? <div className="h-4 w-4 border-2 border-blue-400 dark:border-jewel-teal-tint rounded bg-blue-100 dark:bg-jewel-teal/30 flex-shrink-0" />
                          : <Square className="h-4 w-4 text-gray-300 dark:text-jewel-text-muted flex-shrink-0" />
                        }
                      </button>
                      {/* Employees */}
                      {employees.map(user => (
                        <label
                          key={user.id}
                          className="flex items-center gap-3 px-4 py-2 hover:bg-blue-50/50 dark:hover:bg-jewel-teal/10 cursor-pointer transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={selectedUsers.includes(user.id)}
                            onChange={() => handleToggleUser(user.id)}
                            className="w-4 h-4 text-blue-600 rounded border-gray-300 dark:border-jewel-border focus:ring-blue-500"
                          />
                          <div
                            className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[9px] font-black flex-shrink-0"
                            style={{ backgroundColor: dept.color }}
                          >
                            {(user.name || '?').split(' ').map(n => n[0]).join('').toUpperCase()}
                          </div>
                          <span className="text-sm text-gray-700 dark:text-jewel-text truncate">{user.name}</span>
                        </label>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        {/* Footer */}
        <div className="px-6 py-4 border-t dark:border-jewel-border bg-gray-50 dark:bg-jewel-surface rounded-b-2xl flex items-center justify-between gap-3">
          <p className="text-xs text-gray-400 dark:text-jewel-text-muted">
            Η αναφορά θα ανοίξει σε νέα καρτέλα. Χρησιμοποιήστε <strong>Εκτύπωση → Αποθήκευση ως PDF</strong>.
          </p>
          <div className="flex gap-3 flex-shrink-0">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 dark:text-jewel-text-muted border border-gray-300 dark:border-jewel-border rounded-lg hover:bg-gray-100 dark:hover:bg-jewel-border transition-colors"
            >
              Ακύρωση
            </button>
            <button
              onClick={handleExport}
              disabled={isExporting || matchedCount === 0 || selectedUsers.length === 0 || !startDate || !endDate}
              className="flex items-center gap-2 px-5 py-2 bg-blue-600 dark:bg-jewel-forest text-white text-sm font-bold rounded-lg hover:bg-blue-700 dark:hover:bg-jewel-forest-tint disabled:opacity-40 disabled:cursor-not-allowed transition-all active:scale-95"
            >
              {isExporting
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <Download className="h-4 w-4" />
              }
              Εξαγωγή ({matchedCount})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MassExportModal;